import React from "react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Wind, Waves, Snowflake, Flame, CircleDot, MountainSnow, Ruler } from "lucide-react";
import { Card, Stat, tooltipStyle } from "./shared";

const PROC = {
    fluvial: { label: "Fluvial", icon: Waves },
    aeolian: { label: "Aeolian", icon: Wind },
    glacial: { label: "Glacial", icon: Snowflake },
    volcanic: { label: "Volcanic", icon: Flame },
    impact: { label: "Impact", icon: CircleDot },
    mass_wasting: { label: "Mass wasting", icon: MountainSnow },
};
const LANDFORM_ICON = { ...Object.fromEntries(Object.entries(PROC).map(([k, v]) => [k, v.icon])), tectonic: Ruler, seasonal: Waves };

export default function GeomorphPanel({ data }) {
    const g = data.geomorphology;
    const radar = Object.entries(g.processes).map(([k, v]) => ({ process: PROC[k].label, value: v }));
    const dominant = Object.entries(g.processes).sort((a, b) => b[1] - a[1])[0][0];
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title="Surface-process signature" icon={Wind} accent="text-ares-cyan" testid="geomorph-radar-card">
                <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={radar} outerRadius="75%">
                            <PolarGrid stroke="#2a2e3d" />
                            <PolarAngleAxis dataKey="process" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                            <Radar dataKey="value" stroke="#00e5ff" fill="#00e5ff" fillOpacity={0.25} />
                            <Tooltip {...tooltipStyle} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
                <p className="text-sm text-ares-muted">Dominant process: <span className="text-ares-cyan font-medium" data-testid="geomorph-dominant">{PROC[dominant].label}</span></p>
            </Card>
            <div className="space-y-5">
                <Card title="Morphometry (from MOLA)" icon={Ruler} testid="geomorph-morphometry-card">
                    <div className="grid grid-cols-3 gap-3">
                        <Stat label="Elevation" value={data.elevation_m.toLocaleString()} unit="m" testid="geomorph-elevation" />
                        <Stat label="Mean slope" value={data.slope_deg} unit="°" testid="geomorph-slope" />
                        <Stat label="Roughness" value={data.roughness} testid="geomorph-roughness" />
                    </div>
                </Card>
                <Card title="Landform inventory" testid="geomorph-landforms-card">
                    <div className="space-y-2">
                        {g.landforms.map((l, i) => {
                            const Icon = LANDFORM_ICON[l.process.split(" ")[0]] || CircleDot;
                            return (
                                <div key={i} className="flex items-center gap-3 rounded-md border border-ares-border bg-ares-panel/60 px-3 py-2" data-testid={`geomorph-landform-${i}`}>
                                    <Icon className="w-4 h-4 text-ares-cyan shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">{l.name}</p>
                                        <p className="font-mono text-[10px] uppercase tracking-wider text-ares-muted">{l.process.replace("_", " ")}</p>
                                    </div>
                                    <span className="font-mono text-[11px] text-ares-bright text-right">{l.scale}</span>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>
        </div>
    );
}
